/**
 * Registre des 32 mini-jeux. Chacun se déclare ici avec un `load()` lazy
 * pour ne pas bloater le bundle initial avec 32 composants.
 *
 * Convention : `type` est la clé stable côté DB (`games.type`). Une fois
 * livrée, ne JAMAIS la renommer (les leaderboards / achievements en dépendent).
 */
const definitions = [
    // ── Démo de framework / smoke test ──────────────────────────────────────
    {
        type: 'reaction-test',
        name: 'Réflexe Olympique Solo',
        description: '8 manches qui s\'accélèrent. Enchaîne 3 réactions sub-200 ms pour déclencher un combo ×1.5.',
        emoji: '⚡',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            max_score: 1_000_000,
            min_duration_ms: 2_000,
        },
        hidden: true,
        rules: [
            'L\'écran reste rouge pendant un délai aléatoire (entre 0,6 et 4 secondes selon la manche).',
            'Quand il devient VERT, clique le plus vite possible (ou tape ESPACE).',
            'Si tu cliques pendant le rouge, c\'est un faux départ : -200 points et streak réinitialisée.',
            'Score par manche : 1000 points à 0 ms, 0 points à 500 ms ou plus (dégradé linéaire).',
            'Combo : 3 réactions sub-200 ms d\'affilée déclenchent un multiplicateur ×1,5 sur les suivantes.',
            'Le combo est cassé par un faux départ ou une réaction lente (>200 ms).',
            'Les manches s\'accélèrent : les attentes sont plus longues au début (2-4 s) et plus courtes à la fin (0,6-1,5 s).',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Réagir au signal' },
            { keys: 'Entrée', action: 'Passer à la manche suivante' },
        ],
        load: () => import('../reaction-test/ReactionTestGame').then((m) => ({
            Component: m.ReactionTestGame,
        })),
    },
    // ── #1bis Réflexe Olympique — 4 rings avec multi vivant ────────────────
    {
        type: 'arena-reflexe',
        name: 'Réflexe Olympique',
        description: '4 rings de réflexe en parallèle. 8 manches qui s\'accélèrent, combo ×1.5 si tu enchaînes 3 réactions sub-200 ms.',
        emoji: '⚡',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            max_score: 1_000_000,
            min_duration_ms: 2_000,
        },
        rules: [
            'L\'écran reste rouge pendant un délai aléatoire (entre 0,6 et 4 s selon la manche).',
            'Quand il devient VERT, clique le plus vite possible (ou tape ESPACE).',
            'Si tu cliques pendant le rouge = faux départ : -200 pts et streak réinitialisée.',
            'Score par manche : 1000 pts à 0 ms, 0 pts à 500 ms ou plus (dégradé linéaire).',
            'Combo : 3 réactions sub-200 ms d\'affilée → multiplicateur ×1,5 sur les suivantes.',
            'Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion). Multi : tu vois leurs rings en live.',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Réagir au signal' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../arena-reflexe/ArenaReflexeGame').then((m) => ({
            Component: m.ArenaReflexeGame,
        })),
    },
    // ── #28 Frappe Olympique (typing) ────────────────────────────────────────
    {
        type: 'frappe-olympique',
        name: 'Frappe Olympique',
        description: 'Retape le texte le plus vite et le plus précisément possible. Score recalculé serveur (anti-macro).',
        emoji: '⌨️',
        category: 'word',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            difficulty: 'medium',
            // Cible serveur : WPM × accuracy² × multiplier × 10. max_score large.
            max_score: 100_000,
            min_duration_ms: 1_500,
        },
        load: () => import('../frappe-olympique/FrappeOlympiqueGame').then((m) => ({
            Component: m.FrappeOlympiqueGame,
        })),
    },
    // ── #26 Calcul de Vestiaire (mental math) ────────────────────────────────
    {
        type: 'calcul-vestiaire',
        name: 'Calcul de Vestiaire',
        description: 'Additions, soustractions, multiplications en rafale. 20 questions en 60 secondes.',
        emoji: '🧮',
        category: 'word', // pas de catégorie "mental" dans l'enum DB, on regroupe avec word
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            questions: 20,
            total_duration_ms: 60_000,
            max_score: 50_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../calcul-vestiaire/CalculVestiaireGame').then((m) => ({
            Component: m.CalculVestiaireGame,
        })),
    },
    // ── #17 Drapeau Blitz (geo quiz) ─────────────────────────────────────────
    {
        type: 'drapeau-blitz',
        name: 'Drapeau Blitz',
        description: 'Un drapeau s\'affiche, tape le pays. 10 drapeaux en 60 secondes.',
        emoji: '🏳️',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 10,
            total_duration_ms: 60_000,
            max_score: 50_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../drapeau-blitz/DrapeauBlitzGame').then((m) => ({
            Component: m.DrapeauBlitzGame,
        })),
    },
    // ── #1 Rage Button Nage Solo (alternance A/D pure) ──────────────────────
    // type DB stable = 'rage-button-nage' pour préserver l'historique des scores.
    // hidden = true → exclu du catalogue / tournois (la version Multi remplace
    // visuellement). Les anciens scores restent affichables via getMiniGame.
    {
        type: 'rage-button-nage',
        name: 'Rage Button Nage Solo',
        description: 'Spam clavier brut : alterne A et D pendant 30 s, deux fois la même touche = pénalité. Pas de visuel — juste le score.',
        emoji: '⌨️',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 30_000,
            max_score: 50_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'Alterne les touches A et D le plus vite possible pendant 30 secondes.',
            'Chaque alternance correcte = +50 points.',
            'Deux pressions de suite sur la même touche = -10 points (anti-spam à une main).',
            'Score brut, pas de mécanique de visualisation. Version masquée — la version piscine la remplace.',
        ],
        shortcuts: [
            { keys: 'A et D', action: 'Alterne pour nager (au clavier)' },
        ],
        load: () => import('../rage-button-nage/RageButtonNageGame').then((m) => ({
            Component: m.RageButtonNageGame,
        })),
    },
    // ── #1bis Rage Button Nage — piscine, couloirs, IA ou vrais joueurs ─────
    {
        type: 'course-de-natation',
        name: 'Rage Button Nage',
        description: 'Piscine 4 couloirs. Alterne A/D pour avancer ; bats les IA en solo, tes amis en multi (positions live).',
        emoji: '🏊',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 30_000,
            pool_strokes: 50,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'Alterne A et D le plus vite possible — chaque alternance fait avancer ton nageur dans son couloir.',
            'Score = brasses × 20. Atteindre le mur (50 brasses) = 1000 points, et tu peux continuer à scorer au-delà jusqu\'à la fin du timer.',
            'La partie ne s\'arrête qu\'à la fin des 30 secondes — le mur est un repère, pas une ligne d\'arrivée bloquante.',
            'Solo : 3 IA t\'accompagnent (Tortue, Espoir, Champion) avec des cadences fixes.',
            'Multi : tu vois tes amis nager en parallèle — leurs positions s\'updatent en temps réel.',
            'Deux pressions de suite sur la même touche = aucune avancée (perte de tempo, pas de pénalité dure).',
        ],
        shortcuts: [
            { keys: 'A et D', action: 'Alterne pour nager' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../course-de-natation/CourseDeNatationGame').then((m) => ({
            Component: m.CourseDeNatationGame,
        })),
    },
    // ── #7 Mash to Lift Solo (1 essai, spam + max secret) — masqué ──────────
    // type DB stable pour préserver l'historique. Remplacé visuellement par
    // la version "compétition haltéro" 3 essais avec multi vivant.
    {
        type: 'mash-to-lift',
        name: 'Mash to Lift Solo',
        description: 'Version originale 1 essai : spam le bouton 3 s, mais attention à la limite secrète. Remplacé par la version compète 3 essais.',
        emoji: '🏋️',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 3_000,
            min_max: 80,
            max_max: 130,
            max_score: 5_000,
            min_duration_ms: 1_500,
        },
        load: () => import('../mash-to-lift/MashToLiftGame').then((m) => ({
            Component: m.MashToLiftGame,
        })),
    },
    // ── #7bis Mash to Lift — compétition haltéro 3 essais avec multi vivant ──
    {
        type: 'competition-haltero',
        name: 'Mash to Lift',
        description: 'Compète d\'haltéro 3 essais : tape vite, mais arrête-toi avant de lâcher la barre. Bats les IA en solo, tes amis en multi.',
        emoji: '🏋️',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            attempts: 3,
            attempt_duration_ms: 5_000,
            getready_ms: 1_000,
            cooldown_ms: 1_500,
            min_secret_max: 25,
            max_secret_max: 50,
            max_score: 500,
            min_duration_ms: 5_000,
        },
        rules: [
            'Compète d\'haltérophilie en 3 essais successifs.',
            'À chaque essai : 5 secondes pour spammer Espace (ou cliquer). Plus tu tapes, plus tu marques.',
            'Une LIMITE SECRÈTE existe (entre 25 et 50 taps), unique pour les 3 essais. Si tu la dépasses, tu lâches la barre = essai à 0.',
            'Tu peux STOP à tout moment (Entrée ou bouton vert) pour valider ton score actuel et sécuriser tes points.',
            'Si le timer expire avant que tu stoppe, le count actuel est validé automatiquement.',
            'Score final = somme des 3 essais. Stratégie : "j\'arrête tôt safe" vs "je pousse plus loin et je risque le drop".',
            'Solo : 3 IA t\'accompagnent (Prudent, Régulier, Téméraire — qui souvent lâche par excès). Multi : tu vois les essais de tes amis en live.',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Pump pendant l\'essai' },
            { keys: 'Entrée', action: 'STOP — valider le count actuel' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../competition-haltero/CompetitionHalteroGame').then((m) => ({
            Component: m.CompetitionHalteroGame,
        })),
    },
    // ── #5 Esquive Express Solo (1 ring, signal → réponse) — masqué ─────────
    // Type DB stable pour l'historique. Remplacé visuellement par la version
    // "arène 4 rings" multi vivant.
    {
        type: 'esquive-express',
        name: 'Esquive Express Solo',
        description: 'Version originale 1 ring : esquive ou bloque selon le signal. Remplacé par la version arène 4 rings.',
        emoji: '🥊',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            reaction_window_ms: 1_200,
            max_score: 10_000,
            min_duration_ms: 3_000,
        },
        load: () => import('../esquive-express/EsquiveExpressGame').then((m) => ({
            Component: m.EsquiveExpressGame,
        })),
    },
    // ── #5bis Esquive Express — arène 4 rings de boxe avec multi vivant ─────
    {
        type: 'combat-boxe',
        name: 'Esquive Express',
        description: 'Arène 4 rings : esquive ou bloque les coups en parallèle de 3 IA en solo, ou de tes amis en multi (positions live).',
        emoji: '🥊',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            reaction_window_ms: 1_200,
            max_score: 4_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'À chaque manche, ton adversaire prépare un coup : flèche ← (esquive gauche), flèche → (esquive droite), ou bouclier 🛡 (bloque).',
            'Tu as 1.2 secondes pour taper la bonne touche : ←, → ou ↓ (ou Espace pour bloquer).',
            'Réussir → +200 points + bonus de vitesse (jusqu\'à +200 si réaction quasi-instantanée).',
            'Rater (mauvaise touche ou trop tard) → 0 point pour cette manche, ton boxeur prend le coup.',
            '8 manches au total. Score max théorique : 3200.',
            'Solo : 3 IA t\'accompagnent dans les rings voisins (Tortue, Régulier, Champion). Multi : tu vois tes amis en live.',
        ],
        shortcuts: [
            { keys: '←', action: 'Esquive à gauche' },
            { keys: '→', action: 'Esquive à droite' },
            { keys: '↓ / Espace', action: 'Bloque le coup' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../combat-boxe/CombatBoxeGame').then((m) => ({
            Component: m.CombatBoxeGame,
        })),
    },
    // ── #15 Grip Master Solo (1 paroi, séquence Simon-like) — masqué ────────
    // Type DB stable pour l'historique. Remplacé visuellement par la version
    // "4 parois" multi vivant.
    {
        type: 'grip-master',
        name: 'Grip Master Solo',
        description: 'Version originale 1 paroi : mémorise la séquence de prises et reproduis-la. Remplacé par la version 4 parois.',
        emoji: '🧗',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            start_length: 3,
            max_level: 12,
            show_ms: 600,
            max_score: 5_000,
            min_duration_ms: 3_000,
        },
        load: () => import('../grip-master/GripMasterGame').then((m) => ({
            Component: m.GripMasterGame,
        })),
    },
    // ── #15bis Grip Master — 4 parois d'escalade avec multi vivant ──────────
    {
        type: 'escalade-paroi',
        name: 'Grip Master',
        description: '4 parois en parallèle : mémorise la séquence et reproduis-la. Score = niveau atteint × 200. Bats les IA en solo, tes amis en multi.',
        emoji: '🧗',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            start_length: 3,
            max_level: 12,
            show_ms: 600,
            max_score: 2_400,
            min_duration_ms: 5_000,
        },
        rules: [
            'Une séquence de 3 touches s\'affiche, mémorise-la puis reproduis-la avec ←/↑/↓/→.',
            'Niveau réussi → tu montes d\'un cran sur la paroi, niveau suivant +1 touche dans la séquence.',
            'Une erreur = glissade, fin de la partie. Score = niveau atteint × 200 (max 2400 au sommet).',
            'En solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion) avec différentes capacités de mémorisation.',
            'En multi : tu vois la progression de tes amis sur les parois voisines en temps réel.',
        ],
        shortcuts: [
            { keys: '↑ ↓ ← →', action: 'Reproduire la séquence' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../escalade-paroi/EscaladeParoiGame').then((m) => ({
            Component: m.EscaladeParoiGame,
        })),
    },
    // ── #14 Spike Timing (volley) ────────────────────────────────────────────
    {
        type: 'spike-timing',
        name: 'Spike Timing',
        description: 'La jauge oscille — clique exactement au pic. 5 manches, plus tu es proche du sommet, plus tu marques.',
        emoji: '🏐',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 5,
            cycle_ms: 1_500,
            max_score: 6_000,
            min_duration_ms: 3_000,
        },
        load: () => import('../spike-timing/SpikeTimingGame').then((m) => ({
            Component: m.SpikeTimingGame,
        })),
    },
    // ── #27 Anagramme Sprint (lettres) ───────────────────────────────────────
    {
        type: 'anagramme-sprint',
        name: 'Anagramme Sprint',
        description: 'Reconstitue le mot mélangé. 8 mots en 90 secondes, vocabulaire JO.',
        emoji: '🔠',
        category: 'word',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            total_duration_ms: 90_000,
            max_score: 10_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../anagramme-sprint/AnagrammeSprintGame').then((m) => ({
            Component: m.AnagrammeSprintGame,
        })),
    },
    // ── #22 Qui suis-je ? (devinette) ────────────────────────────────────────
    {
        type: 'qui-suis-je',
        name: 'Qui suis-je ?',
        description: 'Devine la personnalité sportive avec 3 indices progressifs. Plus tôt = plus de points.',
        emoji: '🤔',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 5,
            base_score: 500,
            max_score: 5_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../qui-suis-je/QuiSuisJeGame').then((m) => ({
            Component: m.QuiSuisJeGame,
        })),
    },
    // ── #16 Champion ou Imposteur ? (vrai/faux JO) ───────────────────────────
    {
        type: 'champion-ou-imposteur',
        name: 'Champion ou Imposteur ?',
        description: '10 affirmations sportives — vrai ou faux ? 8s par question, bonus de vitesse.',
        emoji: '🤥',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 10,
            reaction_window_ms: 8_000,
            max_score: 5_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../champion-ou-imposteur/ChampionOuImposteurGame').then((m) => ({
            Component: m.ChampionOuImposteurGame,
        })),
    },
    // ── #29 Bombe à Mots (langage avec timer qui se réduit) ──────────────────
    {
        type: 'bombe-a-mots',
        name: 'Bombe à Mots',
        description: 'Trouve un mot contenant la suite de lettres avant que la bombe explose. Le timer raccourcit à chaque manche.',
        emoji: '💣',
        category: 'word',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 12,
            initial_round_ms: 12_000,
            min_round_ms: 4_000,
            max_score: 10_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../bombe-a-mots/BombeAMotsGame').then((m) => ({
            Component: m.BombeAMotsGame,
        })),
    },
    // ── #11 Jump or Fall Solo (1 terrain défilant) — masqué ────────────────
    {
        type: 'jump-or-fall',
        name: 'Jump or Fall Solo',
        description: 'Version originale 1 terrain : saute les obstacles au bon moment. Remplacé par la version 4 couloirs équestre multi vivant.',
        emoji: '🐴',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            obstacles: 12,
            speed_pct_per_sec: 60,
            min_gap: 22,
            max_gap: 38,
            max_score: 5_000,
            min_duration_ms: 8_000,
        },
        load: () => import('../jump-or-fall/JumpOrFallGame').then((m) => ({
            Component: m.JumpOrFallGame,
        })),
    },
    // ── #11bis Jump or Fall — 4 couloirs avec cavaliers en concours live ────
    {
        type: 'course-equestre',
        name: 'Jump or Fall',
        description: '4 couloirs équestres. Saute les obstacles au bon moment ; bats les IA en solo, tes amis en multi (positions live).',
        emoji: '🐴',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            obstacles: 12,
            obstacle_interval_ms: 1_500,
            hit_window_ms: 250,
            max_score: 4_000,
            min_duration_ms: 8_000,
        },
        rules: [
            'Tes obstacles défilent à un rythme fixe. Tape Espace (ou clique) au bon moment pour sauter.',
            'Saut réussi = +200 + bonus précision (max 100). Obstacle raté = -100 pts.',
            '12 obstacles au total. Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion).',
            'Multi : tu vois tes amis sauter dans les couloirs voisins, animations en temps réel.',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Sauter' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../course-equestre/CourseEquestreGame').then((m) => ({
            Component: m.CourseEquestreGame,
        })),
    },
    // ── #3 Simon dit en rythme Solo (1 zone de pads) — masqué ───────────────
    {
        type: 'simon-rythme',
        name: 'Simon dit en rythme Solo',
        description: 'Version originale 1 zone : mémorise puis reproduis la séquence. Remplacé par la version 4 scènes multi vivant.',
        emoji: '🎵',
        category: 'memory',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            start_length: 3,
            max_level: 12,
            initial_show_ms: 700,
            min_show_ms: 250,
            max_score: 5_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../simon-rythme/SimonRythmeGame').then((m) => ({
            Component: m.SimonRythmeGame,
        })),
    },
    // ── #3bis Simon dit en rythme — 4 scènes en parallèle avec multi vivant ─
    {
        type: 'simon-pads',
        name: 'Simon dit en rythme',
        description: '4 scènes en parallèle : mémorise et reproduis la séquence colorée. Le tempo accélère à chaque niveau.',
        emoji: '🎵',
        category: 'memory',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            start_length: 3,
            max_level: 12,
            initial_show_ms: 700,
            min_show_ms: 250,
            max_score: 3_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'Une séquence de 3 pads colorés (Rouge / Bleu / Jaune / Vert) s\'affiche, mémorise-la puis reproduis-la.',
            'Niveau réussi → niveau suivant +1 pad. Score = niveau atteint × 250.',
            'Le tempo accélère à chaque niveau (700 ms → 250 ms). Une erreur = fin de partie.',
            'Touches : 1/2/3/4 ou Q/W/A/S pour les 4 pads (gauche-droite, haut-bas).',
            'Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion). Multi : tu vois leurs scènes en live.',
        ],
        shortcuts: [
            { keys: '1 / Q', action: 'Pad Rouge' },
            { keys: '2 / W', action: 'Pad Bleu' },
            { keys: '3 / A', action: 'Pad Jaune' },
            { keys: '4 / S', action: 'Pad Vert' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../simon-pads/SimonPadsGame').then((m) => ({
            Component: m.SimonPadsGame,
        })),
    },
    // ── #8 Rock Paper Throw (judo, RPS étendu) ───────────────────────────────
    {
        type: 'rock-paper-throw',
        name: 'Rock Paper Throw',
        description: 'Pierre-feuille-ciseaux version judo : Atemi bat Nage, Nage bat Katame, Katame bat Atemi. 5 échanges contre l\'IA.',
        emoji: '🥋',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 5,
            reveal_ms: 1_400,
            decision_window_ms: 3_000,
            max_score: 5_000,
            min_duration_ms: 3_000,
        },
        load: () => import('../rock-paper-throw/RockPaperThrowGame').then((m) => ({
            Component: m.RockPaperThrowGame,
        })),
    },
    // ── #4 Visée sous pression (tir à l'arc, cible mouvante) ─────────────────
    {
        type: 'visee-sous-pression',
        name: 'Visée sous pression',
        description: 'La cible oscille horizontalement, tire au plus proche du centre. 6 flèches.',
        emoji: '🏹',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            shots: 6,
            speed_pct_per_sec: 35,
            shot_window_ms: 5_000,
            max_score: 8_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../visee-sous-pression/ViseeSousPressionGame').then((m) => ({
            Component: m.ViseeSousPressionGame,
        })),
    },
    // ── #2 Pédalage Infernal Solo (1 pédalier, sans adversaires) — masqué ──
    {
        type: 'pedalage-infernal',
        name: 'Pédalage Infernal Solo',
        description: 'Version originale : 1 pédalier circulaire à tourner avec la souris. Remplacé par la version course 4 couloirs.',
        emoji: '🚴',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 25_000,
            max_score: 30_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../pedalage-infernal/PedalageInfernalGame').then((m) => ({
            Component: m.PedalageInfernalGame,
        })),
    },
    // ── #2bis Pédalage Infernal — 4 couloirs avec cyclistes en course live ──
    {
        type: 'course-cycliste',
        name: 'Pédalage Infernal',
        description: 'Course cycliste 4 couloirs. Tourne ta souris en cercles dans le pédalier ; bats les IA en solo, tes amis en multi (positions live).',
        emoji: '🚴',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 25_000,
            visual_max_tours: 50,
            max_score: 20_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'Tourne ta souris (ou ton doigt sur mobile) en cercles dans la zone du pédalier — clockwise ou anti, peu importe.',
            'Chaque tour complet = +200 points. 25 secondes pour faire un max de tours.',
            'Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion) avec différentes cadences.',
            'Multi : tu vois tes amis pédaler en parallèle dans les couloirs voisins, positions live.',
        ],
        shortcuts: [
            { keys: 'Souris / Doigt', action: 'Tourner en cercles dans le pédalier' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../course-cycliste/CourseCyclisteGame').then((m) => ({
            Component: m.CourseCyclisteGame,
        })),
    },
    // ── #6 Slalom Dingue Solo (1 piste défilante) — masqué ─────────────────
    {
        type: 'slalom-dingue',
        name: 'Slalom Dingue Solo',
        description: 'Version originale 1 piste : portes G/D défilantes. Remplacé par la version 4 couloirs ski multi vivant.',
        emoji: '⛷️',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            gates: 16,
            speed_pct_per_sec: 50,
            min_gap: 22,
            max_gap: 32,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../slalom-dingue/SlalomDingueGame').then((m) => ({
            Component: m.SlalomDingueGame,
        })),
    },
    // ── #6bis Slalom Dingue — 4 couloirs avec skieurs en descente live ──────
    {
        type: 'slalom-couloirs',
        name: 'Slalom Dingue',
        description: '4 couloirs de descente. Tape ←/→ au bon moment pour passer la porte ; bats les IA en solo, tes amis en multi.',
        emoji: '⛷️',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            gates: 16,
            gate_interval_ms: 1_200,
            hit_window_ms: 300,
            max_score: 4_500,
            min_duration_ms: 5_000,
        },
        rules: [
            'Les portes apparaissent à intervalle fixe. Tape ← (gauche) ou → (droite) selon la couleur de la porte.',
            'Bonne touche = +200 + bonus précision (max 100). Mauvaise touche ou ratée = -50 pts.',
            '16 portes au total. Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion).',
            'Multi : tu vois tes amis descendre dans les couloirs voisins, animations en temps réel.',
        ],
        shortcuts: [
            { keys: '← / →', action: 'Tourner gauche / droite' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../slalom-couloirs/SlalomCouloirsGame').then((m) => ({
            Component: m.SlalomCouloirsGame,
        })),
    },
    // ── #9 Ride the Wave Solo (1 vague) — masqué ───────────────────────────
    {
        type: 'ride-the-wave',
        name: 'Ride the Wave Solo',
        description: 'Version originale 1 surfer : maintiens-le dans la zone verte. Remplacé par la version 4 couloirs surf multi vivant.',
        emoji: '🏄',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 30_000,
            zone_half: 12,
            max_score: 8_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../ride-the-wave/RideTheWaveGame').then((m) => ({
            Component: m.RideTheWaveGame,
        })),
    },
    // ── #9bis Ride the Wave — 4 couloirs avec surfeurs sur la même vague ────
    {
        type: 'surf-vague',
        name: 'Ride the Wave',
        description: '4 surfeurs sur la même vague. Maintiens-toi dans la zone verte avec ↑/↓ ou la souris ; bats les IA en solo, tes amis en multi.',
        emoji: '🏄',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 30_000,
            zone_half: 12,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        rules: [
            'La vague (zone verte) bouge verticalement de façon fluide. Maintiens ton surfer dedans avec ↑/↓ ou la souris.',
            'Score = temps cumulé dans la zone × multiplicateur. Toutes les ms passées dans la zone comptent.',
            'Solo : 3 IA t\'accompagnent sur la même vague (Tortue, Régulier, Champion).',
            'Multi : tu vois la position de tes amis sur la vague en temps réel.',
        ],
        shortcuts: [
            { keys: '↑ / ↓', action: 'Monter / descendre le surfer' },
            { keys: 'Souris', action: 'Position du surfer' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../surf-vague/SurfVagueGame').then((m) => ({
            Component: m.SurfVagueGame,
        })),
    },
    // ── #24 Plus ou Moins (comparaison stats) ────────────────────────────────
    {
        type: 'plus-ou-moins',
        name: 'Plus ou Moins',
        description: 'B est-il PLUS ou MOINS que A ? 10 questions, streak multiplie le score (jusqu\'à ×2).',
        emoji: '⚖️',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 10,
            reaction_window_ms: 8_000,
            max_score: 4_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../plus-ou-moins/PlusOuMoinsGame').then((m) => ({
            Component: m.PlusOuMoinsGame,
        })),
    },
    // ── #30 Mot le plus long (grille de lettres) ─────────────────────────────
    {
        type: 'mot-le-plus-long',
        name: 'Mot le plus long',
        description: '9 lettres, 60 secondes. Tape un max de mots formés à partir de cette grille. Score = longueur × 50.',
        emoji: '🔤',
        category: 'word',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 60_000,
            letters: 9,
            max_score: 20_000,
            min_duration_ms: 10_000,
        },
        load: () => import('../mot-le-plus-long/MotLePlusLongGame').then((m) => ({
            Component: m.MotLePlusLongGame,
        })),
    },
    // ── #10 Souffle & Tir (biathlon : sprint + tir) ──────────────────────────
    {
        type: 'souffle-et-tir',
        name: 'Souffle & Tir',
        description: '3 manches alternant sprint (mash) puis tir (mire qui tremble selon le cardio). Trouve l\'équilibre.',
        emoji: '🎯',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 3,
            sprint_ms: 4_000,
            shot_ms: 4_000,
            max_score: 6_000,
            min_duration_ms: 10_000,
        },
        load: () => import('../souffle-et-tir/SouffleEtTirGame').then((m) => ({
            Component: m.SouffleEtTirGame,
        })),
    },
    // ── #23 Roue des Sports (méta-quiz : roue + catégories) ──────────────────
    {
        type: 'roue-des-sports',
        name: 'Roue des Sports',
        description: 'La roue tourne sur une catégorie (Géo / Records / Histoire / Surprise), tu réponds en QCM. 8 tours.',
        emoji: '🎡',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            reaction_window_ms: 12_000,
            spin_ms: 1_400,
            max_score: 5_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../roue-des-sports/RoueDesSportsGame').then((m) => ({
            Component: m.RoueDesSportsGame,
        })),
    },
    // ── #31 Memory & Spot (mode paires) ──────────────────────────────────────
    {
        type: 'memory-and-spot',
        name: 'Memory & Spot',
        description: 'Retrouve les 8 paires d\'emojis sportifs en un minimum de tentatives. Score = 6000 − temps − erreurs.',
        emoji: '🧠',
        category: 'memory',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            pairs: 8,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../memory-and-spot/MemoryAndSpotGame').then((m) => ({
            Component: m.MemoryAndSpotGame,
        })),
    },
    // ── #20 Qui a gagné l'or ? (palmarès JO + compétitions majeures) ─────────
    {
        type: 'qui-a-gagne-or',
        name: 'Qui a gagné l\'or ?',
        description: 'QCM sur les vainqueurs de grandes épreuves (JO, Mondiaux, F1, foot…). 8 questions, plus tu réponds vite plus tu marques.',
        emoji: '🥇',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 8,
            reaction_window_ms: 12_000,
            max_score: 5_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../qui-a-gagne-or/QuiAGagneOrGame').then((m) => ({
            Component: m.QuiAGagneOrGame,
        })),
    },
    // ── #19 Placez la Ville ! (clic carte monde) ─────────────────────────────
    {
        type: 'placez-la-ville',
        name: 'Placez la Ville !',
        description: 'Clique sur la carte à l\'emplacement de la ville (capitales JO + grandes villes sportives). 6 villes.',
        emoji: '🌍',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 6,
            reaction_window_ms: 15_000,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../placez-la-ville/PlacezLaVilleGame').then((m) => ({
            Component: m.PlacezLaVilleGame,
        })),
    },
    // ── #12 Duel Lumineux Solo (1 zone) — masqué ───────────────────────────
    {
        type: 'duel-lumineux',
        name: 'Duel Lumineux Solo',
        description: 'Version originale 1 zone : signal vert/rouge avec feintes. Remplacé par la version 4 rings escrime multi vivant.',
        emoji: '⚔️',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 10,
            feint_probability: 0.25,
            reaction_window_ms: 1_000,
            max_score: 7_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../duel-lumineux/DuelLumineuxGame').then((m) => ({
            Component: m.DuelLumineuxGame,
        })),
    },
    // ── #12bis Duel Lumineux — 4 rings d'escrime avec multi vivant ──────────
    {
        type: 'arena-escrime',
        name: 'Duel Lumineux',
        description: '4 pistes d\'escrime en parallèle. Frappe au signal vert, retiens-toi sur le rouge (feinte). 10 touches.',
        emoji: '⚔️',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 10,
            feint_probability: 0.25,
            reaction_window_ms: 1_000,
            max_score: 4_500,
            min_duration_ms: 5_000,
        },
        rules: [
            'À chaque manche : signal VERT = frappe (Espace), signal ROUGE = feinte (NE PAS frapper).',
            'Bonne touche = +300 + bonus vitesse. Feinte évitée = +250.',
            'Faux départ ou frappe-feinte = -200 pts.',
            '10 touches au total. Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion).',
            'Multi : tu vois leurs pistes en live, animations en temps réel.',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Frapper' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../arena-escrime/ArenaEscrimeGame').then((m) => ({
            Component: m.ArenaEscrimeGame,
        })),
    },
    // ── #21 Pixel Reveal (grille de tuiles qui disparaissent) ────────────────
    {
        type: 'pixel-reveal',
        name: 'Pixel Reveal',
        description: 'Une image se révèle tuile par tuile. Devine la discipline plus vite que les autres.',
        emoji: '🟦',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 6,
            grid_size: 8,
            reveal_interval_ms: 350,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../pixel-reveal/PixelRevealGame').then((m) => ({
            Component: m.PixelRevealGame,
        })),
    },
    // ── #25 Quiz Photo (zoom progressif) ─────────────────────────────────────
    {
        type: 'quiz-photo',
        name: 'Quiz Photo',
        description: 'Le cadrage dézoome progressivement. Reconnais la discipline le plus vite possible.',
        emoji: '📷',
        category: 'quiz',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            rounds: 6,
            start_zoom: 7,
            reaction_window_ms: 10_000,
            max_score: 6_000,
            min_duration_ms: 5_000,
        },
        load: () => import('../quiz-photo/QuizPhotoGame').then((m) => ({
            Component: m.QuizPhotoGame,
        })),
    },
    // ── #13 Synchro ou Coule Solo (1 cercle métronome) — masqué ─────────────
    {
        type: 'synchro-ou-coule',
        name: 'Synchro ou Coule Solo',
        description: 'Version originale 1 cercle pulsant : tape en rythme à 80 BPM. Remplacé par la version 4 couloirs aviron multi vivant.',
        emoji: '🚣',
        category: 'sport',
        hidden: true,
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            bpm: 80,
            duration_ms: 30_000,
            intro_ms: 2_000,
            max_score: 8_000,
            min_duration_ms: 10_000,
        },
        load: () => import('../synchro-ou-coule/SynchroOuCouleGame').then((m) => ({
            Component: m.SynchroOuCouleGame,
        })),
    },
    // ── #13bis Synchro ou Coule — 4 couloirs avec rameurs en course live ────
    {
        type: 'synchro-aviron',
        name: 'Synchro ou Coule',
        description: '4 couloirs d\'aviron. Tape en rythme à 80 BPM ; bats les IA en solo, tes amis en multi (positions live).',
        emoji: '🚣',
        category: 'sport',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            bpm: 80,
            duration_ms: 30_000,
            intro_ms: 2_000,
            max_score: 8_000,
            min_duration_ms: 10_000,
        },
        rules: [
            'À 80 BPM, un cercle pulse à chaque battement. Tape Espace (ou clique) au pic.',
            'Précision : <50 ms = PERFECT (200 pts) · <120 ms = GREAT (120) · <250 ms = GOOD (50).',
            'Combo : +5 % par tap consécutif réussi, capé à +50 %. Cassé par un MISS.',
            'Solo : 3 IA t\'accompagnent (Tortue, Régulier, Champion) avec différentes précisions.',
            'Multi : tu vois la progression de tes amis dans les couloirs voisins en temps réel.',
        ],
        shortcuts: [
            { keys: 'Espace / Clic', action: 'Tap au beat' },
        ],
        hasOwnLiveOverlay: true,
        load: () => import('../synchro-aviron/SynchroAvironGame').then((m) => ({
            Component: m.SynchroAvironGame,
        })),
    },
    // ── #32 Skribbl-Cascade (dessin libre — V1 simplifié, cascade en V2) ─────
    {
        type: 'skribbl-cascade',
        name: 'Skribbl-Cascade',
        description: 'Dessine le mot affiché en 60 s. Plus tu finis tôt, plus tu marques. (Cascade téléphone-arabe en V2.)',
        emoji: '🎨',
        category: 'memory',
        minPlayers: 1,
        maxPlayers: 8,
        rankedConfig: {
            duration_ms: 60_000,
            max_score: 1_500,
            min_duration_ms: 5_000,
        },
        load: () => import('../skribbl-cascade/SkribblCascadeGame').then((m) => ({
            Component: m.SkribblCascadeGame,
        })),
    },
    // À venir V2 : blind-hymne (#18 — déféré sans assets audio) +
    // skribbl-cascade mode cascade complet (relai téléphone arabe multi).
];
const byType = new Map(definitions.map((d) => [d.type, d]));
export function getMiniGame(type) {
    return byType.get(type);
}
/**
 * Liste des mini-jeux **visibles** (exclut ceux flaggés `hidden: true`).
 * Utilisée par le catalogue (/games) et la sélection de tournoi.
 * Pour résoudre un type spécifique (anciens scores, etc.), utiliser
 * `getMiniGame(type)` qui ne filtre pas.
 */
export function listMiniGames() {
    return definitions.filter((d) => !d.hidden);
}
